import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JwtService } from 'src/app/services/jwt.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';

@Component({
    selector: 'app-peme-content',
    templateUrl: './peme-content.component.html',
    styleUrls: ['./peme-content.component.css']
})
export class PemeContentComponent implements OnInit, OnDestroy {
    // Form data model
    pemeContent = {
        client: null as any,
        excelFile: null as any,
        zipFile: null as any,
        entrySheetName: '',
        commit: false
    };

    // State management
    uploading = false;
    jobStatus: any = null;
    private pollingInterval: any = null;
    private currentJobId: string | null = null;
    currentStep = 1; // 1: Upload, 2: Processing, 3: Complete

    // Available clients from sessionStorage
    clients = JSON.parse(sessionStorage.getItem('clients'));

    clientControl = new FormControl();
    filteredClients: Observable<any[]>;

    constructor(
        private api: ApiService,
        private snackbar: MatSnackBar,
        private jwt: JwtService,
        private mixpanel: MixpanelService
    ) { }

    ngOnInit(): void {
        this.mixpanel.track('page_view', {
            page: 'PEME Content Upload',
            category: 'Dashboard',
            button: '-'
        });

        this.jwt.check();

        this.filteredClients = this.clientControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterClients(value || ''))
        );
    }

    private _filterClients(value: string): any[] {
        if (typeof value === 'object' && value !== null) {
            return this.clients;
        }
        const filterValue = value.toLowerCase();
        return this.clients.filter((client: any) =>
            client.dname.toLowerCase().includes(filterValue) ||
            client.id.toString().toLowerCase().includes(filterValue)
        );
    }

    displayClient(client: any): string {
        return client?.dname || '';
    }

    onClientSelected(client: any): void {
        this.pemeContent.client = client?.id || null;
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    onExcelFileSelected(event: any): void {
        this.pemeContent.excelFile = event.target.files[0] ?? null;
    }

    onZipFileSelected(event: any): void {
        this.pemeContent.zipFile = event.target.files[0] ?? null;
    }

    removeExcelFile(): void {
        this.pemeContent.excelFile = null;
    }

    removeZipFile(): void {
        this.pemeContent.zipFile = null;
    }

    upload(): void {
        this.mixpanel.track('button_click', {
            page: 'PEME Content Upload',
            category: 'Dashboard',
            button: this.pemeContent.commit ? 'Upload' : 'Validate'
        });

        const formData = new FormData();
        formData.append('client', this.pemeContent.client);
        formData.append('excelFile', this.pemeContent.excelFile);
        formData.append('zipFile', this.pemeContent.zipFile);
        formData.append('entrySheetName', this.pemeContent.entrySheetName);
        formData.append('commit', this.pemeContent.commit ? 'yes' : 'no');

        this.uploading = true;
        this.jobStatus = null;
        this.currentStep = 2;

        this.api.pemeContentIngest(formData).subscribe({
            next: (response) => {
                this.uploading = false;
                console.log('pemeContentIngest response:', response);

                const jobId = response?.data?.jobId;
                if (jobId) {
                    this.snackbar.open(
                        'Upload started! Tracking progress...',
                        undefined,
                        { duration: 3000, panelClass: 'success' }
                    );
                    this.startPolling(jobId);
                } else {
                    this.snackbar.open(
                        'Upload failed: Invalid response from server',
                        undefined,
                        { duration: 5000, panelClass: 'failure' }
                    );
                    this.currentStep = 1;
                }
            },
            error: (error) => {
                this.uploading = false;
                this.currentStep = 1;
                console.error('Upload failed:', error);
            }
        });
    }

    private startPolling(uuid: string): void {
        this.currentJobId = uuid;
        this.checkStatus();
        this.pollingInterval = setInterval(() => {
            this.checkStatus();
        }, 5000);
    }

    private checkStatus(): void {
        if (!this.currentJobId) return;

        this.api.pemeContentStatus(this.currentJobId).subscribe({
            next: (response) => {
                console.log('pemeContentStatus response:', response);
                const data = response?.data || response;
                if (data) {
                    this.jobStatus = data;

                    if (
                        response.data.status === 'SUCCESS' ||
                        response.data.status === 'FAILED'
                    ) {
                        this.stopPolling();
                        this.currentStep = 3;

                        if (response.data.status === 'SUCCESS') {
                            this.snackbar.open(
                                'Upload completed successfully!',
                                undefined,
                                { duration: 5000, panelClass: 'success' }
                            );
                        } else {
                            this.snackbar.open(
                                'Upload failed. Please check the error details.',
                                undefined,
                                { duration: 5000, panelClass: 'failure' }
                            );
                        }
                    }
                }
            },
            error: (error) => {
                console.error('Status check failed:', error);
            }
        });
    }

    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    promoteToUpload(): void {
        this.pemeContent.commit = true;
        this.upload();
    }

    resetForm(): void {
        this.pemeContent = {
            client: null,
            excelFile: null,
            zipFile: null,
            entrySheetName: '',
            commit: false
        };
        this.clientControl.reset();
        this.jobStatus = null;
        this.currentJobId = null;
        this.currentStep = 1;
        this.stopPolling();
    }
}
